import { ApiServiceFactory } from '@/factories/api-service.factory'
import type { BasicAuth } from '@/ts/types/basic-auth.type'
import type { PartnerConnectConfiguration, PartnerConnectIntegration, PartnerConnectResponse } from '@/ts/types/partner-connect.types'

const createAuthConfig = (basicAuth?: BasicAuth): Record<string, unknown> => {
  if (!basicAuth?.username || !basicAuth.password) return {}
  return { auth: { username: basicAuth.username, password: basicAuth.password } }
}

export const createPartnerConnectService = (basicAuth?: BasicAuth) => {
  const service = new ApiServiceFactory().createApiService(
    () => {},
    { Accept: 'application/json', 'Content-Type': 'application/json' },
    'partner-connect-service',
    import.meta.env.VITE_PARTNER_CONNECT_BASE_URL,
    60000
  )

  const getIntegration = async (partnerId: string, integrationId: string): Promise<PartnerConnectIntegration> => {
    const response = await service.get(`api/v2/partners/${partnerId}/integrations/${integrationId}`, createAuthConfig(basicAuth))
    const integration = (response.data as PartnerConnectResponse<PartnerConnectIntegration> | null)?.data
    if (!integration || typeof integration !== 'object' || Array.isArray(integration) || integration.uuid !== integrationId) {
      throw new Error('[Partner Connect]: expected a matching integration')
    }
    return integration
  }

  /** Compatibility route used when no integration ID is given. */
  const getLegacyConfig = async (partnerId: string): Promise<PartnerConnectConfiguration> => {
    const response = await service.get(`api/v1/leasing-calculator/config/${partnerId}`, createAuthConfig(basicAuth))
    const integrations = (response.data as PartnerConnectResponse<PartnerConnectIntegration[]>).data
    const configuration = Array.isArray(integrations) && integrations.length === 1 ? integrations[0]?.configuration : undefined
    if (!configuration || typeof configuration !== 'object') throw new Error('[Partner Connect]: invalid config response')
    return configuration
  }

  return { getIntegration, getLegacyConfig }
}
